import React, { useState, useEffect, useCallback } from 'react';
import OpenpaySubscriptionConfig from '../components/OpenpaySubscriptionConfig';
import styles from './DegreesSection.module.css';

const API = process.env.REACT_APP_API_URL || '/api';

function authHeaders() {
  const token = localStorage.getItem('odm_token');
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

const EMPTY_FORM = {
  slug: '',
  type: 'Bachillerato',
  name: '',
  bannerImageS3: '',
  syllabusUrl: '',
  informationUrl: '',
  isActive: true,
  heroHighlights: [{ value: '', label: '' }],
  platformDescription: '',
  learningObjects: [''],
  targetAudience: { description: '', idealFor: [''] },
  lms: { name: '', description: '', features: [''] },
  generalInfo: { duration: '', schedule: '', subjects: '', graduation: '' },
  costs: { tuition: '', enrollment: '', reenrollment: '', graduation: '' },
};

function ListField({ label, items, onChange }) {
  const update = (i, val) => { const a = [...items]; a[i] = val; onChange(a); };
  const add = () => onChange([...items, '']);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className={styles.listField}>
      <p className={styles.listLabel}>{label}</p>
      {items.map((item, i) => (
        <div key={i} className={styles.listRow}>
          <input className={styles.input} value={item} onChange={(e) => update(i, e.target.value)} placeholder={`${label} ${i + 1}`} />
          {items.length > 1 && <button type="button" className={styles.removeBtn} onClick={() => remove(i)}>✕</button>}
        </div>
      ))}
      <button type="button" className={styles.addBtn} onClick={add}>+ Agregar</button>
    </div>
  );
}

function HeroHighlightsField({ items, onChange }) {
  const update = (i, key, val) => { const a = [...items]; a[i] = { ...a[i], [key]: val }; onChange(a); };
  const add = () => onChange([...items, { value: '', label: '' }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className={styles.listField}>
      <p className={styles.listLabel}>Hero Highlights (banner)</p>
      {items.map((item, i) => (
        <div key={i} className={styles.listRow}>
          <input className={styles.inputSm} value={item.value} onChange={(e) => update(i, 'value', e.target.value)} placeholder="Valor (ej. 6 semestres)" style={{ flex: 1 }} />
          <input className={styles.inputSm} value={item.label} onChange={(e) => update(i, 'label', e.target.value)} placeholder="Etiqueta (ej. Duración)" style={{ flex: 1 }} />
          {items.length > 1 && <button type="button" className={styles.removeBtn} onClick={() => remove(i)}>✕</button>}
        </div>
      ))}
      <button type="button" className={styles.addBtn} onClick={add}>+ Highlight</button>
    </div>
  );
}

export default function BachilleratoSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('list');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchAll = useCallback(() => {
    setLoading(true);
    fetch(`${API}/bachillerato`, { headers: authHeaders() })
      .then((r) => r.json())
      .then(({ data }) => setItems(data || []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setError(''); setView('form'); };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      slug: item.slug || '',
      type: item.type || 'Bachillerato',
      name: item.name || '',
      bannerImageS3: item.bannerImageS3 || '',
      syllabusUrl: item.syllabusUrl || '',
      informationUrl: item.informationUrl || '',
      isActive: item.isActive ?? true,
      heroHighlights: item.heroHighlights?.length
        ? item.heroHighlights.map((h) => ({ value: h.value || '', label: h.label || '' }))
        : [{ value: '', label: '' }],
      platformDescription: item.platformDescription || '',
      learningObjects: item.learningObjects?.length ? item.learningObjects : [''],
      targetAudience: {
        description: item.targetAudience?.description || '',
        idealFor: item.targetAudience?.idealFor?.length ? item.targetAudience.idealFor : [''],
      },
      lms: {
        name: item.lms?.name || '',
        description: item.lms?.description || '',
        features: item.lms?.features?.length ? item.lms.features : [''],
      },
      generalInfo: {
        duration: item.generalInfo?.duration || '',
        schedule: item.generalInfo?.schedule || '',
        subjects: item.generalInfo?.subjects || '',
        graduation: item.generalInfo?.graduation || '',
      },
      costs: {
        tuition: item.costs?.tuition || '',
        enrollment: item.costs?.enrollment || '',
        reenrollment: item.costs?.reenrollment || '',
        graduation: item.costs?.graduation || '',
      },
    });
    setError('');
    setView('form');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este bachillerato?')) return;
    await fetch(`${API}/bachillerato/${id}`, { method: 'DELETE', headers: authHeaders() });
    fetchAll();
  };

  const set = (path, value) => {
    setForm((prev) => {
      const next = { ...prev };
      const keys = path.split('.');
      let ref = next;
      for (let i = 0; i < keys.length - 1; i++) { ref[keys[i]] = { ...ref[keys[i]] }; ref = ref[keys[i]]; }
      ref[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    const payload = {
      ...form,
      heroHighlights: form.heroHighlights.filter((h) => h.value || h.label),
      learningObjects: form.learningObjects.filter(Boolean),
      targetAudience: {
        ...form.targetAudience,
        idealFor: form.targetAudience.idealFor.filter(Boolean),
      },
      lms: {
        ...form.lms,
        features: form.lms.features.filter(Boolean),
      },
    };
    try {
      const url = editing ? `${API}/bachillerato/${editing._id}` : `${API}/bachillerato`;
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) { setError(data.message || 'Error al guardar.'); return; }
      setView('list');
      fetchAll();
    } catch {
      setError('No se pudo conectar con el servidor.');
    } finally {
      setSaving(false);
    }
  };

  if (view === 'list') {
    return (
      <div className={styles.container}>
        <div className={styles.listHeader}>
          <h1 className={styles.pageTitle}>Bachillerato</h1>
          <button type="button" className={styles.primaryBtn} onClick={openCreate}>+ Nuevo bachillerato</button>
        </div>
        {loading ? <p className={styles.hint}>Cargando...</p> : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead><tr><th>Nombre</th><th>Tipo</th><th>Slug</th><th>Activo</th><th>Acciones</th></tr></thead>
              <tbody>
                {items.map((d) => (
                  <tr key={d._id}>
                    <td>{d.name}</td><td>{d.type}</td>
                    <td className={styles.slugCell}>{d.slug}</td>
                    <td>{d.isActive ? '✅' : '❌'}</td>
                    <td><div className={styles.actions}>
                      <button type="button" className={styles.editBtn} onClick={() => openEdit(d)}>Editar</button>
                      <button type="button" className={styles.deleteBtn} onClick={() => handleDelete(d._id)}>Eliminar</button>
                    </div></td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan={5} className={styles.hint}>Sin registros.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.listHeader}>
        <h1 className={styles.pageTitle}>{editing ? 'Editar bachillerato' : 'Nuevo bachillerato'}</h1>
        <button type="button" className={styles.backBtn} onClick={() => setView('list')}>← Volver</button>
      </div>
      <form className={styles.form} onSubmit={handleSubmit}>
        <section className={styles.formSection}>
          <h2 className={styles.sectionTitle}>Información básica</h2>
          <div className={styles.grid2}>
            <label className={styles.label}>Nombre *<input className={styles.input} value={form.name} onChange={(e) => set('name', e.target.value)} required /></label>
            <label className={styles.label}>Slug *<input className={styles.input} value={form.slug} onChange={(e) => set('slug', e.target.value)} required /></label>
            <label className={styles.label}>Tipo<input className={styles.input} value={form.type} onChange={(e) => set('type', e.target.value)} /></label>
            <label className={styles.label}>URL Banner (S3)<input className={styles.input} value={form.bannerImageS3} onChange={(e) => set('bannerImageS3', e.target.value)} placeholder="https://..." /></label>
            <label className={styles.label}>URL del temario<input type="url" className={styles.input} value={form.syllabusUrl} onChange={(e) => set('syllabusUrl', e.target.value)} placeholder="https://.../temario.pdf" /></label>
            <label className={styles.label}>Ruta informativa<input className={styles.input} value={form.informationUrl} onChange={(e) => set('informationUrl', e.target.value)} placeholder="oferta?type=bachillerato" /></label>
          </div>
          <label className={styles.checkRow}><input type="checkbox" checked={form.isActive} onChange={(e) => set('isActive', e.target.checked)} />Activo</label>
        </section>

        <section className={styles.formSection}>
          <h2 className={styles.sectionTitle}>Banner</h2>
          <HeroHighlightsField items={form.heroHighlights} onChange={(v) => set('heroHighlights', v)} />
        </section>

        <section className={styles.formSection}>
          <h2 className={styles.sectionTitle}>Plataforma</h2>
          <label className={styles.label}>Descripción de la plataforma
            <textarea className={styles.textarea} rows={3} value={form.platformDescription} onChange={(e) => set('platformDescription', e.target.value)} />
          </label>
          <ListField label="Objetos de aprendizaje" items={form.learningObjects} onChange={(v) => set('learningObjects', v)} />
        </section>

        <section className={styles.formSection}>
          <h2 className={styles.sectionTitle}>Audiencia objetivo</h2>
          <label className={styles.label}>Descripción
            <textarea className={styles.textarea} rows={3} value={form.targetAudience.description} onChange={(e) => set('targetAudience.description', e.target.value)} />
          </label>
          <ListField label="Ideal para" items={form.targetAudience.idealFor} onChange={(v) => set('targetAudience.idealFor', v)} />
        </section>

        <section className={styles.formSection}>
          <h2 className={styles.sectionTitle}>LMS (Plataforma)</h2>
          <div className={styles.grid2}>
            <label className={styles.label}>Nombre<input className={styles.input} value={form.lms.name} onChange={(e) => set('lms.name', e.target.value)} placeholder="Schology" /></label>
          </div>
          <label className={styles.label}>Descripción<textarea className={styles.textarea} rows={3} value={form.lms.description} onChange={(e) => set('lms.description', e.target.value)} /></label>
          <ListField label="Características" items={form.lms.features} onChange={(v) => set('lms.features', v)} />
        </section>

        <section className={styles.formSection}>
          <h2 className={styles.sectionTitle}>Información general</h2>
          <div className={styles.grid2}>
            <label className={styles.label}>Duración<input className={styles.input} value={form.generalInfo.duration} onChange={(e) => set('generalInfo.duration', e.target.value)} /></label>
            <label className={styles.label}>Horario<input className={styles.input} value={form.generalInfo.schedule} onChange={(e) => set('generalInfo.schedule', e.target.value)} /></label>
            <label className={styles.label}>Materias<input className={styles.input} value={form.generalInfo.subjects} onChange={(e) => set('generalInfo.subjects', e.target.value)} /></label>
            <label className={styles.label}>Titulación<input className={styles.input} value={form.generalInfo.graduation} onChange={(e) => set('generalInfo.graduation', e.target.value)} /></label>
          </div>
        </section>

        <section className={styles.formSection}>
          <h2 className={styles.sectionTitle}>Costos</h2>
          <div className={styles.grid2}>
            <label className={styles.label}>Colegiatura<input className={styles.input} value={form.costs.tuition} onChange={(e) => set('costs.tuition', e.target.value)} /></label>
            <label className={styles.label}>Inscripción<input className={styles.input} value={form.costs.enrollment} onChange={(e) => set('costs.enrollment', e.target.value)} /></label>
            <label className={styles.label}>Reinscripción<input className={styles.input} value={form.costs.reenrollment} onChange={(e) => set('costs.reenrollment', e.target.value)} /></label>
            <label className={styles.label}>Titulación<input className={styles.input} value={form.costs.graduation} onChange={(e) => set('costs.graduation', e.target.value)} /></label>
          </div>
        </section>

        {error && <p className={styles.errorMsg}>{error}</p>}
        <div className={styles.formActions}>
          <button type="button" className={styles.backBtn} onClick={() => setView('list')}>Cancelar</button>
          <button type="submit" className={styles.primaryBtn} disabled={saving}>{saving ? 'Guardando...' : 'Guardar bachillerato'}</button>
        </div>

        <OpenpaySubscriptionConfig
          educationalProgramType="bachillerato"
          educationalProgramId={editing?._id}
          tuition={form.costs.tuition}
        />
      </form>
    </div>
  );
}
